/**
 * Подписи и справочники интерфейса ОСНОВА.Кадры: статусы, переходы, графики,
 * источники, часовые пояса объектов, события журнала.
 */
import type {
  KadryAssigneeRole,
  KadryHiringRequest,
  KadryProfileOrigin,
  KadryRequestStatus,
  KadryScheduleKind,
  KadrySourceId,
} from './types';

export type KadryTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

export const KADRY_REQUEST_STATUS_LABELS: Record<KadryRequestStatus, string> = {
  draft: 'Черновик',
  active: 'В работе',
  paused: 'Приостановлена',
  closed: 'Закрыта',
};

export const KADRY_REQUEST_STATUS_TONES: Record<KadryRequestStatus, KadryTone> = {
  draft: 'neutral',
  active: 'success',
  paused: 'warning',
  closed: 'info',
};

/**
 * Заявка из вакансии hh.ru, которую ещё ни разу не запускали (Р22): модуль
 * завёл её на паузе — кадровик проверяет карточку поиска и запускает.
 */
export function kadryRequestJustAdded(
  r: Pick<KadryHiringRequest, 'origin' | 'status' | 'activated_at'>,
): boolean {
  return r.origin === 'hh_import' && r.status === 'paused' && r.activated_at == null;
}

/** Переход заявки по машине состояний Прил. Г. */
export interface KadryRequestTransition {
  to: KadryRequestStatus;
  label: string;
  /** Нужна утверждённая версия профиля поиска (только активация). */
  needsApprovedProfile?: boolean;
  /** Переход необратим — подтверждение с предупреждением. */
  terminal?: boolean;
  variant: 'primary' | 'secondary' | 'danger';
}

/**
 * Переходы заявки: `draft → active ⇄ paused → closed`, черновик можно отменить
 * (`draft → closed`), `closed` терминально. Зеркалит `RequestStatus::can_transition`.
 */
export function kadryRequestTransitions(
  from: KadryRequestStatus,
  /** Заявка ни разу не запускалась (из вакансии hh.ru) — «Запустить», а не «Возобновить». */
  neverStarted = false,
): KadryRequestTransition[] {
  switch (from) {
    case 'draft':
      return [
        { to: 'active', label: 'Запустить в работу', needsApprovedProfile: true, variant: 'primary' },
        { to: 'closed', label: 'Отменить черновик', terminal: true, variant: 'danger' },
      ];
    case 'active':
      return [
        { to: 'paused', label: 'Приостановить', variant: 'secondary' },
        { to: 'closed', label: 'Закрыть заявку', terminal: true, variant: 'danger' },
      ];
    case 'paused':
      return [
        {
          to: 'active',
          label: neverStarted ? 'Запустить в работу' : 'Возобновить',
          needsApprovedProfile: true,
          variant: 'primary',
        },
        { to: 'closed', label: 'Закрыть заявку', terminal: true, variant: 'danger' },
      ];
    case 'closed':
      return [];
  }
}

export const KADRY_ASSIGNEE_ROLE_LABELS: Record<KadryAssigneeRole, string> = {
  recruiter: 'Рекрутер',
  requester: 'Заказчик',
};

export const KADRY_PROFILE_ORIGIN_LABELS: Record<KadryProfileOrigin, string> = {
  manual: 'заполнен вручную',
  copy: 'копия утверждённой версии',
  llm: 'разбор текста заявки',
  rules: 'разбор по правилам',
};

export const KADRY_SCHEDULE_KIND_LABELS: Record<KadryScheduleKind, string> = {
  rotation: 'Вахта',
  shift: 'Сменный график',
  full_time: 'Полный день',
  part_time: 'Частичная занятость',
  flexible: 'Гибкий график',
};

export const KADRY_SCHEDULE_KINDS: readonly KadryScheduleKind[] = [
  'rotation',
  'shift',
  'full_time',
  'part_time',
  'flexible',
];

export const KADRY_SOURCE_LABELS: Record<KadrySourceId, string> = {
  hh: 'hh.ru',
  avito: 'Авито Работа',
  superjob: 'SuperJob',
  trudvsem: 'Работа России',
  internal: 'Кадровый резерв',
};

/** Пояснения к источникам (что источник даёт). */
export const KADRY_SOURCE_HINTS: Partial<Record<KadrySourceId, string>> = {
  trudvsem: 'только рыночные данные, кандидатов не даёт',
  internal: 'собственный резерв и бывшие сотрудники',
};

export const KADRY_SOURCES: readonly KadrySourceId[] = [
  'hh',
  'avito',
  'superjob',
  'trudvsem',
  'internal',
];

/** Часовой пояс объекта: IANA-зона и смещение от Москвы. */
export interface KadryTimezoneOption {
  tz: string;
  /** Смещение от UTC, часов. */
  utc: number;
  city: string;
}

/** IANA-зоны России (объект определяет тихие часы бота и сутки лимитов). */
export const KADRY_RU_TIMEZONES: readonly KadryTimezoneOption[] = [
  { tz: 'Europe/Kaliningrad', utc: 2, city: 'Калининград' },
  { tz: 'Europe/Moscow', utc: 3, city: 'Москва' },
  { tz: 'Europe/Simferopol', utc: 3, city: 'Симферополь' },
  { tz: 'Europe/Kirov', utc: 3, city: 'Киров' },
  { tz: 'Europe/Volgograd', utc: 3, city: 'Волгоград' },
  { tz: 'Europe/Astrakhan', utc: 4, city: 'Астрахань' },
  { tz: 'Europe/Saratov', utc: 4, city: 'Саратов' },
  { tz: 'Europe/Ulyanovsk', utc: 4, city: 'Ульяновск' },
  { tz: 'Europe/Samara', utc: 4, city: 'Самара' },
  { tz: 'Asia/Yekaterinburg', utc: 5, city: 'Екатеринбург' },
  { tz: 'Asia/Omsk', utc: 6, city: 'Омск' },
  { tz: 'Asia/Novosibirsk', utc: 7, city: 'Новосибирск' },
  { tz: 'Asia/Barnaul', utc: 7, city: 'Барнаул' },
  { tz: 'Asia/Tomsk', utc: 7, city: 'Томск' },
  { tz: 'Asia/Novokuznetsk', utc: 7, city: 'Новокузнецк' },
  { tz: 'Asia/Krasnoyarsk', utc: 7, city: 'Красноярск' },
  { tz: 'Asia/Irkutsk', utc: 8, city: 'Иркутск' },
  { tz: 'Asia/Chita', utc: 9, city: 'Чита' },
  { tz: 'Asia/Yakutsk', utc: 9, city: 'Якутск' },
  { tz: 'Asia/Khandyga', utc: 9, city: 'Хандыга' },
  { tz: 'Asia/Vladivostok', utc: 10, city: 'Владивосток' },
  { tz: 'Asia/Ust-Nera', utc: 10, city: 'Усть-Нера' },
  { tz: 'Asia/Magadan', utc: 11, city: 'Магадан' },
  { tz: 'Asia/Sakhalin', utc: 11, city: 'Южно-Сахалинск' },
  { tz: 'Asia/Srednekolymsk', utc: 11, city: 'Среднеколымск' },
  { tz: 'Asia/Kamchatka', utc: 12, city: 'Петропавловск-Камчатский' },
  { tz: 'Asia/Anadyr', utc: 12, city: 'Анадырь' },
];

/** «Екатеринбург · МСК+2 (UTC+5)»; для зоны вне списка — сама зона. */
export function kadryTimezoneLabel(tz: string): string {
  const o = KADRY_RU_TIMEZONES.find((t) => t.tz === tz);
  if (!o) return tz;
  const msk = o.utc - 3;
  const mskPart = msk === 0 ? 'МСК' : `МСК${msk > 0 ? '+' : '−'}${Math.abs(msk)}`;
  return `${o.city} · ${mskPart} (UTC+${o.utc})`;
}

/**
 * События журнала аудита (`kadry_audit_log.action`) — полный список действий,
 * которые пишет сервер (`AuditEvent::new` в `osnova-kadry`). Неизвестное
 * действие показывается кодом, так что новые события не теряются.
 */
export const KADRY_AUDIT_ACTION_LABELS: Record<string, string> = {
  'kadry.request.create': 'Создание заявки',
  'kadry.request.update': 'Изменение заявки',
  'kadry.request.transition': 'Смена статуса заявки',
  'kadry.request.assign': 'Назначение на заявку',
  'kadry.request.unassign': 'Снятие назначения',
  'kadry.profile.draft': 'Черновик профиля поиска',
  'kadry.profile.update': 'Правка профиля поиска',
  'kadry.profile.approve': 'Утверждение профиля поиска',
  'kadry.candidate.view_full': 'Просмотр карточки кандидата',
  'kadry.candidate.delete': 'Удаление кандидата',
  'kadry.consent.obtain': 'Получение согласия',
  'kadry.consent.revoke': 'Отзыв согласия',
  'kadry.consent_text.create': 'Новый текст согласия',
  'kadry.consent_text.approve': 'Утверждение текста согласия',
  'kadry.dsr.create': 'Регистрация обращения субъекта',
  'kadry.dsr.update': 'Изменение обращения субъекта',
  'kadry.dsr.access_package': 'Выдача сведений по обращению',
  'kadry.purpose.update': 'Настройка цели обработки',
  'kadry.processing_profile.update': 'Изменение профиля обработки',
  'kadry.processing_profile.status': 'Статус профиля обработки',
  'kadry.egress.blocked_field': 'Блокировка передачи поля',
  'kadry.breakglass.request': 'Заявка на аварийный доступ',
  'kadry.breakglass.decide': 'Решение по аварийному доступу',
  'kadry.breakglass.revoke': 'Отзыв аварийного доступа',
  'kadry.breakglass.read': 'Чтение по аварийному доступу',
  'kadry.budget.create': 'Создание бюджета',
  'kadry.budget.set_limit': 'Изменение лимита бюджета',
  'kadry.tariff.create': 'Добавление тарифа',
  'kadry.quota.upsert': 'Настройка ресурса квоты',
  'kadry.quota.policy': 'Политика квоты',
  'kadry.operation.reconcile': 'Сверка внешней операции',
  'kadry.operation.resolve': 'Ручное решение по операции',
  'kadry.calendar.set': 'Изменение производственного календаря',
  'kadry.calendar.remove': 'Удаление дня календаря',
  'kadry.secret.set': 'Замена секрета интеграции',
  'kadry.secret.remove': 'Удаление секрета интеграции',
  'kadry.work_site.create': 'Добавление объекта',
  'kadry.work_site.update': 'Изменение объекта',
  'kadry.feature_gate.set': 'Переключение функции',
  'kadry.retention.run': 'Очистка по срокам хранения',
  'kadry.restore.reconciled': 'Сверка после восстановления',
};

/** Группы событий для фильтра журнала (префикс `action`). */
export const KADRY_AUDIT_ACTION_GROUPS: readonly { prefix: string; label: string }[] = [
  { prefix: 'kadry.request.', label: 'Заявки' },
  { prefix: 'kadry.profile.', label: 'Профили поиска' },
  { prefix: 'kadry.candidate.', label: 'Кандидаты' },
  { prefix: 'kadry.consent.', label: 'Согласия' },
  { prefix: 'kadry.consent_text.', label: 'Тексты согласий' },
  { prefix: 'kadry.dsr.', label: 'Обращения субъектов' },
  { prefix: 'kadry.purpose.', label: 'Цели обработки' },
  { prefix: 'kadry.processing_profile.', label: 'Профили обработки' },
  { prefix: 'kadry.egress.', label: 'Передача данных' },
  { prefix: 'kadry.breakglass.', label: 'Аварийный доступ' },
  { prefix: 'kadry.budget.', label: 'Бюджеты' },
  { prefix: 'kadry.tariff.', label: 'Тарифы' },
  { prefix: 'kadry.quota.', label: 'Квоты площадок' },
  { prefix: 'kadry.operation.', label: 'Внешние операции' },
  { prefix: 'kadry.calendar.', label: 'Производственный календарь' },
  { prefix: 'kadry.secret.', label: 'Секреты интеграций' },
  { prefix: 'kadry.work_site.', label: 'Объекты' },
  { prefix: 'kadry.feature_gate.', label: 'Функции (гейты)' },
  { prefix: 'kadry.retention.', label: 'Сроки хранения' },
  { prefix: 'kadry.restore.', label: 'Восстановление' },
];

/** Виды объектов журнала (`kadry_audit_log.entity`). */
export const KADRY_AUDIT_ENTITY_LABELS: Record<string, string> = {
  hiring_request: 'Заявка',
  search_profile: 'Профиль поиска',
  candidate: 'Кандидат',
  consent: 'Согласие',
  consent_text: 'Текст согласия',
  dsr: 'Обращение субъекта',
  purpose_settings: 'Цель обработки',
  processing_profile: 'Профиль обработки',
  breakglass: 'Аварийный доступ',
  budget: 'Бюджет',
  tariff: 'Тариф',
  quota_resource: 'Ресурс квоты',
  external_operation: 'Внешняя операция',
  calendar_day: 'День календаря',
  secret: 'Секрет интеграции',
  work_site: 'Объект',
  feature_gate: 'Функция',
  retention: 'Сроки хранения',
  runtime: 'Модуль',
};

export const KADRY_AUDIT_RESULT_LABELS: Record<string, { label: string; tone: KadryTone }> = {
  ok: { label: 'Выполнено', tone: 'success' },
  denied: { label: 'Отказано', tone: 'danger' },
  error: { label: 'Ошибка', tone: 'warning' },
};

export const KADRY_ACTOR_KIND_LABELS: Record<string, string> = {
  user: 'Пользователь',
  system: 'Система',
  candidate: 'Кандидат',
};
